import Loading from '$components/Loading'
import Pagination from '$components/Pagination'
import {
  useApiListingParams,
  usePermissions,
  useShallowQueryParams
} from '$hooks/actions'
import { useGlobalDashboardUserService } from '$hooks/services'
import { redirect, ROUTE_NAMES } from '$router/config'
import TableRowLink from '$router/TableRowLink'
import { TGlobalDashboardUserListResponse } from '$services/api'
import {
  customTableCell,
  customTableContainer,
  customTableHead,
  pageDescription,
  pageHeader
} from '$styles/common.css'
import { Button } from '@genie-fintech/ui/components'
import {
  table,
  tableBody,
  tableContainerInner,
  tableRow
} from '@genie-fintech/ui/style/element'
import { Plus } from 'lucide-react'
import { useCallback, useEffect } from 'react'
import { toClassNames } from '../../../../.@genie-fintech/ui/functions'
import {
  buttonContainer,
  container,
  headerStyle,
  mainStyle,
  searchBoxStyle,
  tableCellStyle,
  tableHeaderFilterStyle
} from './styles.css'
import { removeDashWithSpace } from '$app/utils'
import { useGlobalUserListStore } from '$hooks/stores'
import { useUnmount } from 'ahooks'
import { useRouteSummary } from '$contexts/RouteContext/hooks'
import SearchBox from '$components/SearchBox'
import { COMPANY_NAME } from '$constants/themeTexts'
import { PERMISSIONS } from '$app/constants/common'

const columns: {
  key: Exclude<keyof TGlobalDashboardUserListResponse['data'][number], 'id'>
  value: string
}[] = [
  { key: 'name', value: 'NAME' },
  { key: 'email', value: 'EMAIL' },
  { key: 'type', value: 'ACCOUNT TYPE' },
  { key: 'role', value: 'ACCOUNT ROLE' }
]
const GlobalDashboardUser = () => {
  const { route } = useRouteSummary()
  const currentRoute = route.name
  const { tenantId = '' } = route.params
  const { q = '' } = route.queryParams

  const { updateShallowQueryParams } = useShallowQueryParams()

  const storedSearchValue = useGlobalUserListStore(state => state.searchValue)
  const updateStoredSearchValue = useGlobalUserListStore(
    state => state.setSearchValue
  )
  const resetStoredSearchValue = useGlobalUserListStore(
    state => state.resetSearchValue
  )

  const {
    fetchGlobalDashboardUserList,
    fetchingGlobalDashboardUserList,
    dashboardUsers: { list, meta }
  } = useGlobalDashboardUserService()

  const { checkPermissions } = usePermissions()

  const CAN_CREATE = checkPermissions([PERMISSIONS.MEMBER_CREATE])

  const {
    updateSearchValue,
    debouncedSearchValue,
    searchValue,
    pagerProps,
    perPage
  } = useApiListingParams({
    total: meta?.total,
    perPage: 10,
    initialSearchValue: storedSearchValue || q
  })

  const { currentPage: page } = pagerProps

  useEffect(() => {
    if (!tenantId) return
    fetchGlobalDashboardUserList({
      page,
      per_page: perPage,
      q: debouncedSearchValue
    })
  }, [
    tenantId,
    page,
    perPage,
    debouncedSearchValue,
    fetchGlobalDashboardUserList
  ])

  useEffect(() => {
    updateShallowQueryParams({ q: debouncedSearchValue })
    updateStoredSearchValue(debouncedSearchValue)
  }, [debouncedSearchValue, updateShallowQueryParams, updateStoredSearchValue])

  useUnmount(() => {
    const nextRoute = window.location.pathname.split('/')[2]
    if (currentRoute !== nextRoute) {
      resetStoredSearchValue()
    }
  })

  const onAddUser = useCallback(() => {
    redirect(ROUTE_NAMES.GLOBAL_MEMBER_CREATE, options => options)
  }, [])

  return (
    <article className={container}>
      <header className={headerStyle}>
        <h3 className={pageHeader}>
          Total Members - {meta?.total.toLocaleString()}
        </h3>
        <p className={pageDescription}>
          All the users of different admin or service roles in {COMPANY_NAME}{' '}
          are here.
        </p>
      </header>
      <main className={mainStyle}>
        <article className={tableHeaderFilterStyle}>
          <div className={searchBoxStyle}>
            <SearchBox
              value={searchValue}
              onChange={updateSearchValue}
              placeholder="Search here..."
            />
          </div>
          {CAN_CREATE && (
            <div className={buttonContainer}>
              <Button onClick={onAddUser}>
                <Plus size={16} />
                <span className="whitespace-nowrap">Add User</span>
              </Button>
            </div>
          )}
        </article>
        <article className={customTableContainer}>
          <main className={tableContainerInner}>
            <table className={table({ separator: 'line' })}>
              <thead className={customTableHead}>
                <tr className={tableRow}>
                  <th className={customTableCell} style={{ width: 0 }}>
                    NO
                  </th>
                  {columns.map((col, key) => (
                    <th key={key} className={customTableCell}>
                      {col.value}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={tableBody}>
                {list.map((data, rowKey) => (
                  <TableRowLink
                    key={rowKey}
                    className={tableRow}
                    to={ROUTE_NAMES.GLOBAL_MEMBER_INDEX}
                    options={options => ({
                      ...options,
                      params: { ...options.params, memberId: data.id }
                    })}
                  >
                    <td
                      className={toClassNames(tableCellStyle, customTableCell)}
                    >
                      {(page - 1) * perPage + rowKey + 1}
                    </td>
                    {columns.map((col, colKey) => (
                      <td
                        key={colKey}
                        className={toClassNames(
                          tableCellStyle,
                          customTableCell
                        )}
                      >
                        {(() => {
                          if (['type', 'role'].includes(col.key))
                            return (
                              <span style={{ textTransform: 'capitalize' }}>
                                {removeDashWithSpace(data[col.key])}
                              </span>
                            )

                          return data[col.key] ?? 'N/A'
                        })()}
                      </td>
                    ))}
                  </TableRowLink>
                ))}

                {!list.length && !fetchingGlobalDashboardUserList && (
                  <tr className={tableRow}>
                    <td
                      colSpan={columns.length + 1}
                      style={{ textAlign: 'center' }}
                      className={customTableCell}
                    >
                      NO DATA
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </main>
        </article>

        {fetchingGlobalDashboardUserList && <Loading />}

        {!!list.length && <Pagination meta={meta} pagerProps={pagerProps} />}
      </main>
    </article>
  )
}

export default GlobalDashboardUser
